import { useCallback, type FC } from "react";
import { Controller, useWatch, type Control } from "react-hook-form";

import { useDetailsInlineSaving } from "@ds/components/details-view/hooks";
import { Dropdown, type DropdownChangeEvent } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { useOnMouseEventPreventDefaultHandler } from "@ds/utils/handlers";
import { isFormationPropertyRequired } from "@ds/utils/validation";

import {
  type FormationChangeHandlerDecorator,
  type FormationChangeHandlerInputData,
  type FormationControlFormValues,
  type FormationControlName,
} from "./types";

export const DropdownFormationControl: FC<{
  control: Control<FormationControlFormValues>;
  name: FormationControlName;
  property: TemplateDropdownFormationProperty;
  autoFocus?: boolean;
  excludeLabel?: boolean;
  onBeforeChangeHandlerDecorator: FormationChangeHandlerDecorator;
  onBlur?: () => void;
}> = ({ control, name, property, autoFocus, excludeLabel, onBeforeChangeHandlerDecorator, onBlur }) => {
  const onBlurHandler = useDetailsInlineSaving(onBlur);
  const onMouseDownHandler = useOnMouseEventPreventDefaultHandler();
  const propertyValue = (useWatch({ name, control }) as EntityDropdownFormationProperty)?.value;

  // Build dropdown options from property options
  const options = (property.metadata?.options || []).map(item => {
    // Ensure we create plain objects with string values
    return {
      label: String(item.label || item.value),
      value: String(item.value),
    };
  });

  const onChangeHandler = useCallback(
    (onChange: (inputData: FormationChangeHandlerInputData) => void) => (e: DropdownChangeEvent) => {
      const decoratedOnChange = onBeforeChangeHandlerDecorator(
        onChange,
        propertyValue ?? null,
        () => e.value as string,
      );
      decoratedOnChange(e.value as string);
    },
    [propertyValue, onBeforeChangeHandlerDecorator],
  );

  return (
    <Controller
      name={`${name}.value`}
      control={control}
      render={({ field: { ref, value, onChange, onBlur: onBlurFn, ...restFields }, fieldState }) => {
        const element = (
          <Dropdown
            inputRef={ref}
            value={value}
            options={options}
            optionLabel="label"
            optionValue="value"
            autoFocus={autoFocus}
            onChange={onChangeHandler(onChange)}
            onBlur={onBlurHandler(onBlurFn)}
            onMouseDown={onMouseDownHandler}
            {...restFields}
          />
        );

        if (excludeLabel) {
          return element;
        }

        return (
          <LabelWrapper
            label={property.title}
            className="col-8"
            isRequired={isFormationPropertyRequired(property.metadata?.validation_rules)}
            error={fieldState.error}
          >
            {element}
          </LabelWrapper>
        );
      }}
    />
  );
};
